import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, apiError } from "@/lib/api";
import { fmtCVE0 } from "@/lib/format";
import { fileUrl } from "@/lib/site";
import { Logo } from "@/lib/brand";
import { DemoPage } from "@/components/demo/DemoPage";
import { OwnerBar } from "@/components/site/OwnerBar";
import { QuoteRequestForm } from "@/components/site/QuoteRequestForm";
import {
  CircleNotch, MapPin, Clock, Phone, WhatsappLogo, InstagramLogo, CalendarPlus, CheckCircle, ForkKnife, Star, ClipboardText,
} from "@phosphor-icons/react";

const todayStr = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const waUrl = (n) => {
  let d = (n || "").replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.length === 7) d = "238" + d;
  return `https://wa.me/${d}`;
};

function Hero({ s }) {
  return (
    <header className="relative">
      <div className="h-56 w-full overflow-hidden bg-[rgb(var(--ps-hero))] sm:h-80">
        {s.cover_id && <img src={fileUrl(s.cover_id)} alt="" className="h-full w-full object-cover" fetchpriority="high" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[rgb(var(--ps-shade)/0.9)] via-[rgb(var(--ps-shade)/0.3)] to-transparent" />
      </div>
      <div className="absolute inset-x-0 bottom-0 mx-auto flex max-w-3xl items-end gap-4 px-5 pb-5">
        {s.logo_id && (
          <img src={fileUrl(s.logo_id)} alt={s.name} className="h-20 w-20 shrink-0 rounded-2xl border-4 border-[rgb(var(--ps-bg))] bg-white object-cover shadow-lg sm:h-24 sm:w-24" />
        )}
        <h1 className="ps-serif text-3xl font-semibold leading-tight text-white sm:text-5xl" data-testid="public-site-name">{s.name}</h1>
      </div>
    </header>
  );
}

function Actions({ s }) {
  const items = [
    s.phone && { href: `tel:${s.phone}`, icon: Phone, label: "Ligar", id: "call" },
    s.whatsapp && { href: waUrl(s.whatsapp), icon: WhatsappLogo, label: "WhatsApp", id: "whatsapp" },
    s.instagram && { href: `https://instagram.com/${s.instagram}`, icon: InstagramLogo, label: "Instagram", id: "instagram" },
    s.address && { href: `https://maps.google.com/?q=${encodeURIComponent(s.address)}`, icon: MapPin, label: "Mapa", id: "map" },
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1">
      {items.map((a) => (
        <a key={a.id} href={a.href} target="_blank" rel="noreferrer" data-testid={`public-action-${a.id}`}
          className="flex shrink-0 items-center gap-1.5 rounded-full border border-[rgb(var(--ps-border))] bg-white px-4 py-2 text-sm font-medium text-[rgb(var(--ps-ink))] transition-colors hover:border-[rgb(var(--ps-accent))] hover:text-[rgb(var(--ps-accent))]">
          <a.icon size={18} weight="duotone" /> {a.label}
        </a>
      ))}
    </div>
  );
}

function Info({ s }) {
  return (
    <section className="space-y-4">
      {s.description && <p className="whitespace-pre-line text-[15px] leading-relaxed text-[rgb(var(--ps-ink-2))]" data-testid="public-site-description">{s.description}</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        {s.hours && (
          <div className="flex gap-3 rounded-2xl bg-white p-4"><Clock size={22} className="shrink-0 text-[rgb(var(--ps-accent))]" />
            <div><div className="text-xs font-semibold uppercase tracking-wider text-[rgb(var(--ps-muted))]">Horário</div>
              <div className="whitespace-pre-line text-sm" data-testid="public-site-hours">{s.hours}</div></div></div>
        )}
        {s.address && (
          <div className="flex gap-3 rounded-2xl bg-white p-4"><MapPin size={22} className="shrink-0 text-[rgb(var(--ps-accent))]" />
            <div><div className="text-xs font-semibold uppercase tracking-wider text-[rgb(var(--ps-muted))]">Morada</div>
              <div className="text-sm" data-testid="public-site-address">{s.address}</div></div></div>
        )}
      </div>
    </section>
  );
}

function Menu({ menu, title = "Carta" }) {
  const groups = useMemo(() => {
    const m = new Map();
    menu.forEach((i) => { const c = i.category || "Outros"; if (!m.has(c)) m.set(c, []); m.get(c).push(i); });
    return [...m.entries()];
  }, [menu]);
  const [active, setActive] = useState(null);
  if (!menu.length) return null;
  const cur = active ?? groups[0][0];
  const list = groups.find(([c]) => c === cur)?.[1] || [];
  return (
    <section id="carta" className="space-y-4" data-testid="public-menu">
      <h2 className="ps-serif text-2xl font-semibold">{title}</h2>
      {groups.length > 1 && (
        <div className="sticky top-0 z-10 -mx-5 flex gap-2 overflow-x-auto bg-[rgb(var(--ps-bg)/0.95)] px-5 py-2 backdrop-blur">
          {groups.map(([c]) => (
            <button key={c} onClick={() => setActive(c)} data-testid={`public-menu-cat-${c}`}
              className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${c === cur ? "bg-[rgb(var(--ps-chip))] text-white" : "bg-white text-[rgb(var(--ps-ink-2))]"}`}>{c}</button>
          ))}
        </div>
      )}
      <ul className="divide-y divide-[rgb(var(--ps-divider))] rounded-2xl bg-white">
        {list.map((i) => (
          <li key={i.id} className="flex gap-3 p-4" data-testid={`public-menu-item-${i.id}`}>
            {i.photo_id && <img src={fileUrl(i.photo_id)} alt={i.name} loading="lazy" className="h-20 w-20 shrink-0 rounded-xl object-cover" />}
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="font-semibold text-[rgb(var(--ps-ink))]">{i.name}</h3>
                {i.price > 0 && <span className="whitespace-nowrap text-sm font-bold text-[rgb(var(--ps-accent))]">{fmtCVE0(i.price)}</span>}
              </div>
              {i.description && <p className="mt-1 text-sm leading-snug text-[rgb(var(--ps-muted-2))]">{i.description}</p>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Gallery({ ids }) {
  if (!ids?.length) return null;
  return (
    <section className="space-y-4" data-testid="public-gallery">
      <h2 className="ps-serif text-2xl font-semibold">Galeria</h2>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {ids.map((id, i) => (
          <a key={id} href={fileUrl(id)} target="_blank" rel="noreferrer" className={`overflow-hidden rounded-xl ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
            <img src={fileUrl(id)} alt="" loading="lazy" className="aspect-square h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
          </a>
        ))}
      </div>
    </section>
  );
}

function Reviews({ s }) {
  if (!s.google_reviews?.length) return null;
  return (
    <section className="space-y-4" data-testid="public-reviews">
      <h2 className="ps-serif text-2xl font-semibold">Avaliações Google</h2>
      {s.google_rating && <p className="flex items-center gap-1 text-sm"><Star weight="fill" className="text-amber-500" /> <b>{s.google_rating}</b> {s.google_reviews_count ? `· ${s.google_reviews_count} avaliações` : ""}</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        {s.google_reviews.map((r, i) => (
          <blockquote key={i} className="rounded-2xl bg-white p-4 text-sm">
            <p className="leading-relaxed text-[rgb(var(--ps-ink-2))]">“{r.text}”</p>
            <footer className="mt-2 text-xs text-[rgb(var(--ps-muted))]">{r.author || "Cliente Google"}{r.rating ? ` · ${r.rating}★` : ""}</footer>
          </blockquote>
        ))}
      </div>
    </section>
  );
}

function MapBlock({ s }) {
  if (!s.maps_url || !s.address) return null;
  return (
    <section className="space-y-3" data-testid="public-map">
      <h2 className="ps-serif text-2xl font-semibold">Onde estamos</h2>
      <iframe title="Mapa" loading="lazy" className="h-64 w-full rounded-2xl border-0"
        src={`https://maps.google.com/maps?q=${encodeURIComponent(`${s.name}, ${s.address}`)}&output=embed`} />
      <a href={s.maps_url} target="_blank" rel="noreferrer" className="text-sm font-medium text-[rgb(var(--ps-accent))] underline">Abrir no Google Maps</a>
    </section>
  );
}

function DemoBanner() {
  return (
    <div className="sticky top-0 z-50 bg-[#0E4B47] px-4 py-2 text-center text-xs font-semibold text-white sm:text-sm" data-testid="demo-banner">
      Demonstração criada por GestaCV — Tavares Groupe
    </div>
  );
}

const EMPTY = { date: "", time: "", people: 2, name: "", phone: "", special_request: "", website: "" };
const inputCls = "block w-full min-w-0 max-w-full appearance-none rounded-xl border border-[rgb(var(--ps-border))] bg-white px-4 py-3 text-[15px] outline-none transition-colors focus:border-[rgb(var(--ps-accent))] [&::-webkit-date-and-time-value]:text-left";
const PS_QUOTE_UI = {
  card: "rounded-2xl bg-white", field: inputCls, muted: "text-[rgb(var(--ps-muted-2))]", accentText: "text-[rgb(var(--ps-accent))]",
  button: "rounded-full bg-[rgb(var(--ps-accent))] text-white transition-colors hover:bg-[rgb(var(--ps-accent-hover))]",
};

function ReservationForm({ slug }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[\d\s+()-]{7,20}$/.test(f.phone.trim())) return setError("Indique um telefone válido.");
    setBusy(true);
    try {
      await axios.post(`${API}/public/site/${slug}/reservations`, { ...f, people: Number(f.people) });
      setDone(true);
      setF(EMPTY);
    } catch (err) {
      setError(apiError(err, "Não foi possível enviar a reserva."));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-2xl bg-white p-8 text-center" data-testid="reservation-success">
        <CheckCircle size={48} weight="fill" className="text-emerald-600" />
        <h3 className="ps-serif text-xl font-semibold">Pedido enviado!</h3>
        <p className="text-sm text-[rgb(var(--ps-muted-2))]">Vamos confirmar a sua reserva em breve por telefone ou WhatsApp.</p>
        <button onClick={() => setDone(false)} className="mt-2 text-sm font-medium text-[rgb(var(--ps-accent))] underline" data-testid="reservation-new-btn">Fazer outra reserva</button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-3 rounded-2xl bg-white p-5" data-testid="reservation-form">
      <label className="col-span-1 min-w-0 space-y-1 text-sm font-medium">Data
        <input type="date" required min={todayStr()} value={f.date} onChange={set("date")} className={inputCls} data-testid="reservation-date" /></label>
      <label className="col-span-1 min-w-0 space-y-1 text-sm font-medium">Hora
        <input type="time" required value={f.time} onChange={set("time")} className={inputCls} data-testid="reservation-time" /></label>
      <label className="col-span-2 space-y-1 text-sm font-medium sm:col-span-1">Nº de pessoas
        <input type="number" required min="1" max="50" value={f.people} onChange={set("people")} className={inputCls} data-testid="reservation-people" /></label>
      <label className="col-span-2 space-y-1 text-sm font-medium sm:col-span-1">Nome
        <input required minLength={2} maxLength={80} value={f.name} onChange={set("name")} className={inputCls} autoComplete="name" data-testid="reservation-name" /></label>
      <label className="col-span-2 space-y-1 text-sm font-medium">Telefone / WhatsApp
        <input type="tel" required maxLength={20} value={f.phone} onChange={set("phone")} placeholder="+238 ..." className={inputCls} autoComplete="tel" data-testid="reservation-phone" /></label>
      <label className="col-span-2 space-y-1 text-sm font-medium">Pedido especial <span className="font-normal text-[rgb(var(--ps-muted))]">(opcional)</span>
        <textarea rows={3} maxLength={500} value={f.special_request} onChange={set("special_request")} className={inputCls} data-testid="reservation-special" /></label>
      <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      {error && <p className="col-span-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="reservation-error">{error}</p>}
      <button type="submit" disabled={busy} data-testid="reservation-submit"
        className="col-span-2 flex items-center justify-center gap-2 rounded-full bg-[rgb(var(--ps-accent))] py-3.5 text-base font-semibold text-white transition-colors hover:bg-[rgb(var(--ps-accent-hover))] disabled:opacity-60">
        {busy ? <CircleNotch size={20} className="animate-spin" /> : "Enviar pedido de reserva"}
      </button>
    </form>
  );
}

export default function PublicSite({ demo = false }) {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!demo) return undefined;
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { m.remove(); };
  }, [demo]);

  useEffect(() => {
    const preview = new URLSearchParams(window.location.search).get("preview") === "1";
    axios.get(`${API}/public/${demo ? "demo" : "site"}/${slug}${demo && preview ? "?preview=true" : ""}`).then(({ data }) => {
      setS(data);
      document.title = data.name;
    }).catch(() => setErr(true));
  }, [slug, demo]);

  if (err) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-[rgb(var(--ps-bg))] p-6 text-center ps-classico" data-testid="public-site-not-found">
      <ForkKnife size={40} className="text-[rgb(var(--ps-accent))]" /><h1 className="ps-serif text-2xl">Site não encontrado</h1>
    </div>
  );
  if (!s) return <div className="ps-classico flex min-h-screen items-center justify-center bg-[rgb(var(--ps-bg))]"><CircleNotch size={28} className="animate-spin text-[rgb(var(--ps-accent))]" /></div>;
  if (!demo && s.layout) return <><OwnerBar slug={slug} /><DemoPage s={s} official slug={slug} /></>;
  const booking = s.contact_mode !== undefined ? s.contact_mode === "reservas" : s.reservations_enabled;
  const quote = s.contact_mode === "orcamento";

  return (
    <div className={`public-site ps-${s.theme === "verde" ? "verde" : "classico"} min-h-screen bg-[rgb(var(--ps-bg))] pb-28 text-[rgb(var(--ps-ink))]`} data-testid="public-site" data-theme={s.theme || "classico"}>
      {!demo && <OwnerBar slug={slug} />}
      {s.demo && <DemoBanner />}
      <Hero s={s} />
      <main className="mx-auto max-w-3xl space-y-10 px-5 pt-5">
        <Actions s={s} />
        <Info s={s} />
        <Menu menu={s.menu} title={quote || ((s.demo || s.sticky_whatsapp) && !(booking || s.demo_reservations)) ? "Serviços" : "Carta"} />
        <Gallery ids={s.gallery} />
        <Reviews s={s} />
        <MapBlock s={s} />
        {s.demo_reservations && (
          <section className="space-y-2 rounded-2xl bg-white p-5 text-sm" data-testid="demo-reservations-note">
            <h2 className="ps-serif text-2xl font-semibold">Reservar</h2>
            <p className="text-[rgb(var(--ps-muted-2))]">Aqui os clientes poderão fazer reservas online. Na demonstração o formulário está desativado; quando o site for oficial, as reservas entram diretamente no painel GestaCV.</p>
          </section>
        )}
        {booking && (
          <section id="reservar" className="scroll-mt-4 space-y-4">
            <h2 className="ps-serif text-2xl font-semibold">Reservar mesa</h2>
            <ReservationForm slug={s.slug} />
          </section>
        )}
        {quote && (
          <section id="orcamento" className="scroll-mt-4 space-y-4" data-testid="quote-section">
            <h2 className="ps-serif text-2xl font-semibold">Pedir orçamento</h2>
            <QuoteRequestForm slug={s.slug} ui={PS_QUOTE_UI} />
          </section>
        )}
        <footer className="flex items-center justify-center gap-1.5 pb-4 text-center text-xs text-[rgb(var(--ps-muted))]">© {new Date().getFullYear()} {s.name} · Feito com <Logo className="h-4 w-auto" testid="public-site-gesta-logo" /></footer>
      </main>
      {(booking || quote) && (
        <div className="fixed inset-x-0 bottom-0 z-40 bg-gradient-to-t from-[rgb(var(--ps-bg))] via-[rgb(var(--ps-bg)/0.9)] to-transparent px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-6">
          <a href={booking ? "#reservar" : "#orcamento"} data-testid={booking ? "sticky-reserve-btn" : "sticky-quote-btn"}
            className="mx-auto flex max-w-md items-center justify-center gap-2 rounded-full bg-[rgb(var(--ps-cta))] py-4 text-base font-semibold text-[rgb(var(--ps-cta-ink))] shadow-xl transition-transform active:scale-[0.98]">
            {booking ? <CalendarPlus size={20} weight="bold" /> : <ClipboardText size={20} weight="bold" />} {booking ? "Reservar" : "Pedir orçamento"}
          </a>
        </div>
      )}
      {s.sticky_whatsapp && s.whatsapp && (
        <a href={waUrl(s.whatsapp)} target="_blank" rel="noreferrer" aria-label="WhatsApp" data-testid="sticky-whatsapp-btn"
          className={`fixed right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl transition-transform hover:scale-105 ${(booking || quote) ? "bottom-24" : "bottom-5"}`}>
          <WhatsappLogo size={30} weight="fill" />
        </a>
      )}
    </div>
  );
}
